import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { login, logout } from "../auth";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "../styles/Navbar.css";

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [theme, setTheme] = useState("dark");

  /* =========================
     Navigation Configuration
  ========================= */

  const navItems = useMemo(
    () => [
      { id: "hero", label: "Home", type: "scroll" },
      { id: "programs", label: "Programs", type: "scroll" },
      { id: "gallery", label: "Gallery", type: "scroll" },
      { id: "contact", label: "Contact", type: "scroll" },

      { id: "slots", label: "Online Classes", type: "route", path: "/slots" },

      {
        id: "achievements",
        label: "Achievements",
        type: "menu",
        path: "/achievements",
        children: [
          { label: "GWR Achievements", path: "/achievements/gwr" },
          { label: "Gymnastics/RG", path: "/achievements/rhythmic" },
        ],
      },
    ],
    []
  );

  /* =========================
     Login State
  ========================= */

  useEffect(() => {
    const token = localStorage.getItem("idToken");
    setIsLoggedIn(!!token);
  }, [location]);

  /* =========================
     Theme
  ========================= */

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const initialTheme = savedTheme === "light" ? "light" : "dark";
    setTheme(initialTheme);
    document.documentElement.setAttribute("data-theme", initialTheme);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    localStorage.setItem("theme", nextTheme);
    document.documentElement.setAttribute("data-theme", nextTheme);
  };

  /* =========================
     Scroll Section Detection
  ========================= */

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(null);
      return;
    }

    const scrollItems = navItems.filter((item) => item.type === "scroll");

    const sections = scrollItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);

    let ticking = false;

    const updateActiveSection = () => {
      const viewportCenter = window.innerHeight / 2;

      let nextActive = sections[0]?.id || "hero";
      let closestDistance = Number.POSITIVE_INFINITY;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const distance = Math.abs(center - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          nextActive = section.id;
        }
      });

      setActiveSection(nextActive);
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActiveSection);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    updateActiveSection();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname, navItems]);

  /* =========================
     Scroll Navigation
  ========================= */

  const scrollToSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
    }

    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 120);
  };

  /* =========================
     Navbar
  ========================= */

  return (
    <nav className="navbar navbar-expand-lg material-nav">
      <div className="container-fluid nav-shell">
        <Link className="navbar-brand nav-brand" to="/">
          <img src="logo.avif" alt="Chennai Hoopers logo" className="nav-logo" />
          <span>Chennai Hoopers</span>
        </Link>

        <button
          className="navbar-toggler material-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto nav-links">

            {navItems.map((item) => {
              /* Scroll Sections */

              if (item.type === "scroll") {
                return (
                  <li className="nav-item" key={item.id}>
                    <button
                      className={
                        activeSection === item.id
                          ? "nav-btn active"
                          : "nav-btn"
                      }
                      onClick={() => scrollToSection(item.id)}
                    >
                      {item.label}
                    </button>
                  </li>
                );
              }

              /* Route Links */

              if (item.type === "route") {
                return (
                  <li className="nav-item" key={item.id}>
                    <Link
                      className={
                        location.pathname === item.path
                          ? "nav-btn active"
                          : "nav-btn"
                      }
                      to={item.path}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              }

              /* Hover Dropdown Menu */

              if (item.type === "menu") {
                return (
                  <li className="nav-item achievement-menu" key={item.id}>
                    <Link
                      className={
                        (location.pathname === item.path ||
                          location.pathname.startsWith(item.path + "/"))
                          ? "nav-btn active"
                          : "nav-btn"
                      }
                      to={item.path}
                    >
                      {item.label}
                    </Link>

                    <div className="achievement-dropdown">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          className="dropdown-item"
                          to={child.path}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </li>
                );
              }

              return null;
            })}

          </ul>

          <div className="nav-actions">
            {!isLoggedIn ? (
              <button className="auth-btn" onClick={login}>
                Sign In
              </button>
            ) : (
              <button className="auth-btn" onClick={logout}>
                Logout
              </button>
            )}

            <button
              className="theme-icon-btn"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2.5M12 19.5V22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M2 12h2.5M19.5 12H22M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24">
                  <path d="M19 14.5A7.5 7.5 0 1 1 9.5 5a6.2 6.2 0 0 0 9.5 9.5z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;